package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.localeInfo
import dev.lingvo.shared.screenshots.doc.BrandKit
import dev.lingvo.shared.screenshots.doc.ScreenshotDesign
import dev.lingvo.shared.screenshots.templates.Template
import dev.lingvo.shared.screenshots.templates.TemplateCategory
import dev.lingvo.site.components.studio.preview.DesignLooksPort
import dev.lingvo.site.components.studio.preview.PreviewResources
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LConfirmDialog
import dev.lingvo.site.ds.LConfirmTone
import dev.lingvo.site.ds.LModal
import dev.lingvo.site.ds.LModalSize
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionHeading
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LToggleChip
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import org.jetbrains.compose.web.dom.Div

/** Fixed gallery columns make thumbnail size predictable at the review breakpoints. */
val DesignTemplateGridStyle = CssStyle {
    base { Modifier.styleModifier { property("display", "grid"); property("grid-template-columns", "minmax(0, 1fr)"); property("gap", Space.MD.toString()); property("min-width", "0") } }
    Breakpoint.MD { Modifier.styleModifier { property("grid-template-columns", "repeat(2, minmax(0, 1fr))") } }
    Breakpoint.XL { Modifier.styleModifier { property("grid-template-columns", "repeat(4, minmax(0, 1fr))") } }
}

/** Builds a sample with real plan bindings, never a synthetic production screenshot. */
@Composable
internal fun LayoutSample(data: DesignPageData, template: Template, device: DeviceClass, locale: String, looks: DesignLooksPort, priority: Boolean = false, resources: PreviewResources? = null) {
    val design = remember(data.plan, template, looks) {
        runCatching { DesignModel(data.plan, looks).build(template, data.design?.brand ?: BrandKit(data.project.name.take(60))) }.getOrNull()
    }
    if (design != null) ShotPreviewGrid(data, design, device, locale, looks, onFits = {}, allLocales = false, sample = true, priority = priority, firstOnly = true, sharedResources = resources)
    else LMuted(t("app.projects.slug.screenshots.template_preview_modal.unavailable", "This template couldn't be previewed for this plan."))
}

/** Template use is one guarded design save and returns only after the server acknowledges it. */
@Composable
internal fun TemplatesGallery(
    data: DesignPageData, existing: ScreenshotDesign?, device: DeviceClass, locale: String, looks: DesignLooksPort,
    canWrite: Boolean, params: ScreenshotsAreaState, onParams: (ScreenshotsAreaState) -> Unit,
    onUse: (Template) -> ScreenshotDesign?, saveState: DesignSaveState, saveVersion: Int,
) {
    val ctx = rememberPageContext()
    var preview by remember { mutableStateOf<Template?>(null) }
    var pending by remember { mutableStateOf<Template?>(null) }
    var application by remember { mutableStateOf<Pair<Int, ScreenshotDesign>?>(null) }
    val applying = application != null
    val resources = remember(data.project.id, device, locale, looks) { PreviewResources(looks) }
    DisposableEffect(resources) { onDispose { resources.close() } }
    fun apply(template: Template) { onUse(template)?.let { application = saveVersion to it } }
    val model = remember(looks) { TemplatesModel(looks.templates()) }
    LaunchedEffect(saveState, saveVersion, existing, application) {
        if (application != null && saveState == DesignSaveState.CLEAN && saveVersion > application!!.first && existing == application!!.second) {
            application = null
            ctx.router.tryRoutingTo(screenshotsPath(data.project.slug, ScreenshotsTab.DESIGN, params))
        }
        if (application != null && saveState == DesignSaveState.CLEAN && saveVersion > application!!.first && existing != application!!.second) application = null
    }
    Column(Modifier.fillMaxWidth().gap(Space.LG)) {
        Row(Modifier.gap(Space.SM).styleModifier { property("flex-wrap", "wrap") }) {
            LToggleChip(t("app.projects.slug.screenshots.template_preview_modal.all", "All"), params.category == null, { onParams(params.copy(category = null)) })
            TemplateCategory.entries.forEach { category ->
                val label = when (category) {
                    TemplateCategory.LAYOUT -> t("app.projects.slug.screenshots.template_preview_modal.layouts", "Layouts")
                    TemplateCategory.MINIMAL -> t("app.projects.slug.screenshots.template_preview_modal.minimal", "Minimal")
                    TemplateCategory.BOLD -> t("app.projects.slug.screenshots.template_preview_modal.bold", "Bold")
                    TemplateCategory.DARK -> t("app.projects.slug.screenshots.template_preview_modal.dark", "Dark")
                    TemplateCategory.PLAYFUL -> t("app.projects.slug.screenshots.template_preview_modal.playful", "Playful")
                }
                LToggleChip(label, params.category == category.name.lowercase(), { onParams(params.copy(category = category.name.lowercase())) })
            }
        }
        Div(DesignTemplateGridStyle.toModifier().toAttrs()) {
            model.filtered(params.category).forEach { template ->
                var hovered by remember(template.id) { mutableStateOf(false) }
                var focused by remember(template.id) { mutableStateOf(false) }
                Div(attrs = { onMouseEnter { hovered = true }; onMouseLeave { hovered = false }; onFocusIn { focused = true }; onFocusOut { focused = false } }) {
                    LCard(Modifier.fillMaxWidth()) {
                        Column(Modifier.gap(Space.MD)) {
                            LSectionHeading(template.displayName, description = "")
                            LayoutSample(data, template, device, locale, looks, hovered || focused, resources)
                            LButton(t("app.projects.slug.screenshots.template_preview_modal.preview", "Preview with my shots"), onClick = { preview = template }, variant = LButtonVariant.SECONDARY)
                            if (canWrite && !applying) LButton(t("app.projects.slug.screenshots.template_preview_modal.use", "Use this template"), variant = LButtonVariant.SECONDARY, onClick = {
                                if (existing != null) pending = template else apply(template)
                            })
                        }
                    }
                }
            }
        }
        if (looks.available && model.filtered(params.category).isEmpty()) LMuted(t("app.projects.slug.screenshots.template_preview_modal.empty", "No templates in this category."))
        if (applying) LMuted(t("app.projects.slug.screenshots.template_preview_modal.saving", "Saving the template…"))
    }
    TemplatePreviewModal(preview, data, existing, device, locale, looks, onClose = { preview = null })
    LConfirmDialog(pending != null,
        title = t("app.projects.slug.screenshots.template_preview_modal.replace", "Replace the layout?"),
        confirmLabel = t("app.projects.slug.screenshots.template_preview_modal.use", "Use this template"),
        onConfirm = { pending?.let(::apply); pending = null }, onCancel = { pending = null },
        tone = LConfirmTone.NEUTRAL,
        consequenceTitle = t("app.projects.slug.screenshots.template_preview_modal.replacement_summary", "Layout replacement"),
        consequence = tf("app.projects.slug.screenshots.template_preview_modal.consequence", "Replaces the layout of {count} shots. Captions and captures stay. You can restore the previous version.", "count" to data.plan.slots.size))
}

/** Language navigation changes the preview only; captions remain owned by the translation editor. */
@Composable
internal fun TemplatePreviewModal(
    template: Template?, data: DesignPageData, existing: ScreenshotDesign?, device: DeviceClass,
    initialLocale: String, looks: DesignLooksPort, onClose: () -> Unit,
) {
    var locale by remember(template?.id) { mutableStateOf(initialLocale) }
    val locales = (listOf(data.project.sourceLocale) + data.project.locales).distinct()
    val document = remember(template?.id, existing, data.plan) {
        template?.let { selected -> runCatching {
            val model = DesignModel(data.plan, looks)
            if (existing == null) model.build(selected, BrandKit(data.project.name.take(60))) else model.switchLayout(existing, selected)
        }.getOrNull() }
    }
    LModal(template != null, template?.displayName.orEmpty(), onClose, size = LModalSize.LG, modifier = Modifier.styleModifier { property("max-width", "min(80rem, calc(100vw - 32px))") }) {
        if (document == null) LAlert(title = t("app.projects.slug.screenshots.template_preview_modal.failed", "The template preview couldn't load. Try another template."))
        else {
            Row(Modifier.gap(Space.SM).styleModifier { property("flex-wrap", "wrap") }) {
                LSelect(locale, locales.map { LSelectOption(it, localeInfo(it).englishName) }, { locale = it }, label = t("app.projects.slug.screenshots.template_preview_modal.language", "Language"))
                LButton("←", variant = LButtonVariant.SECONDARY, onClick = { locale = locales[(locales.indexOf(locale) - 1 + locales.size) % locales.size] },
                    accessibleLabel = t("app.projects.slug.screenshots.template_preview_modal.previous", "← Previous language"))
                LButton("→", variant = LButtonVariant.SECONDARY, onClick = { locale = locales[(locales.indexOf(locale) + 1) % locales.size] },
                    accessibleLabel = t("app.projects.slug.screenshots.template_preview_modal.next", "→ Next language"))
            }
            ShotPreviewGrid(data, document, device, locale, looks, onFits = {}, allLocales = false, sample = true, priority = true)
        }
    }
}
